import React, { useState, useEffect } from "react";
import { AlertTriangle, ChevronDown, Info } from "lucide-react";
import { RoleHelper, DD_MM_YYYY, hasAnyWarehouseSplit, splitByWarehouseRatio } from "src/helpers";
import { ProductColumnHeader } from "src/enums";
import { POExchangeRates, POProductConfirmation, Product, TableColumn } from "src/types";
import { useAuthorisation } from "src/context";
import { Modal, Table } from "src/components";
import { formatFloatWithTwoDecimals } from "src/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from "src/shadcn/ui/dropdown-menu";
import { CURRENCY_SYMBOL_TO_CODE, EXCHANGE_RATES_TO_GBP } from "src/constants/exchangeRates";

interface ProductTableProps {
  products: Product[];
  productConfirmationData?: POProductConfirmation[];
  vendorLocation?: string;
  hmrcExchangeRates?: POExchangeRates;
}

type Currency = "£" | "$" | "€";

const ProductTable: React.FC<ProductTableProps> = ({
  products,
  productConfirmationData,
  vendorLocation,
  hmrcExchangeRates
}) => {
  const [selectedImage, setSelectedImage] = useState<{ url: string; name: string } | null>(null);
  const { userRole } = useAuthorisation();

  // Default currency based on vendor location and role
  // If role is VENDOR: US vendors use $, Germany vendors use €, otherwise £
  const getDefaultCurrency = (): Currency => {
    if (userRole === "VENDOR" && vendorLocation === "United States") {
      return "$";
    }
    if (userRole === "VENDOR" && vendorLocation === "Germany") {
      return "€";
    }
    return "£";
  };
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(getDefaultCurrency());
  const [selectedHmrcCurrency, setSelectedHmrcCurrency] = useState<Currency>("£");

  // Update currency when vendorLocation or userRole changes
  useEffect(() => {
    setSelectedCurrency(getDefaultCurrency());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorLocation, userRole]);

  const convertPrice = (priceInGBP: number, currency: Currency): number => {
    const currencyCode = CURRENCY_SYMBOL_TO_CODE[currency];
    return priceInGBP / EXCHANGE_RATES_TO_GBP[currencyCode];
  };

  const convertHmrcPrice = (priceInGBP: number, currency: Currency): number => {
    if (currency === "£") return priceInGBP;
    const currencyCode = CURRENCY_SYMBOL_TO_CODE[currency];
    const rate = hmrcExchangeRates?.[currencyCode];
    if (!rate) return priceInGBP;
    return priceInGBP / rate;
  };

  const getCurrencySymbol = (currency: Currency): string => {
    return currency;
  };

  const purchaseOrderProductsColumns: TableColumn<ProductColumnHeader, Product>[] = [];

  // Add image column if any product has an image
  if (products?.some((product) => product.imageUrl)) {
    purchaseOrderProductsColumns.push({
      header: ProductColumnHeader.IMAGE,
      column: (row: Product) => (
        <div className="flex items-center justify-center">
          {row.imageUrl ? (
            <button
              type="button"
              className="h-12 w-12 rounded-lg border border-secondary-200 overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              onClick={() => setSelectedImage({ url: row.imageUrl as string, name: row.name })}
            >
              <img
                src={row.imageUrl}
                alt={row.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  // Hide image on error
                  e.currentTarget.style.display = "none";
                }}
              />
            </button>
          ) : (
            <span className="text-secondary-400 text-xs">-</span>
          )}
        </div>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    });
  }

  if (userRole !== "VENDOR") {
    purchaseOrderProductsColumns.push({
      header: ProductColumnHeader.ID,
      column: (row: Product) => <span className="font-medium text-secondary-900">{row.id}</span>,
      styles: { minWidth: "100px" }
    });
  }

  purchaseOrderProductsColumns.push({
    header: ProductColumnHeader.NAME,
    column: (row) => <span className="font-semibold text-secondary-900">{row.name}</span>,
    styles: { minWidth: "160px" }
  });

  purchaseOrderProductsColumns.push({
    header: ProductColumnHeader.SIZE,
    column: (row) => <span className="text-secondary-700">{row.size || "-"}</span>,
    styles: { minWidth: "90px" }
  });

  if (userRole === "VENDOR" && products?.some((product) => product.extraInfo)) {
    purchaseOrderProductsColumns.push({
      header: ProductColumnHeader.EXTRA_INFO,
      column: (row: Product) => <span className="text-secondary-700">{row.extraInfo || "-"}</span>,
      styles: { minWidth: "120px" }
    });
  }

  if (userRole === "VENDOR" || RoleHelper.isHealfOperationsTeam(userRole) || userRole === "WAREHOUSE") {
    purchaseOrderProductsColumns.push({
      header: ProductColumnHeader.BARCODE,
      column: (row: Product) => <span className="font-mono text-secondary-900">{row.barcode}</span>,
      styles: { minWidth: "140px" }
    });
  }

  if (userRole !== "VENDOR") {
    purchaseOrderProductsColumns.push({
      header: ProductColumnHeader.SKU,
      column: (row: Product) => <span className="text-secondary-700">{row.skuName}</span>,
      styles: { minWidth: "110px" }
    });
  }

  // const isUSVendor = vendorLocation === "United States";
  const shouldShowUnitCost = RoleHelper.isHealfOperationsTeam(userRole) || userRole === "HEALF_ACCOUNTANT";
  // (userRole === "VENDOR" && isUSVendor);

  if (shouldShowUnitCost) {
    purchaseOrderProductsColumns.push({
      header: (
        <div className="flex items-center gap-4">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-help">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning-500" aria-hidden="true" />
                  <span className="text-white">{ProductColumnHeader.UNIT_COST.replace(" (£)", "")}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-warning-200">Exchange Rate Disclaimer</p>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Prices are converted using fixed exchange rates. Due to constantly fluctuating exchange rates, the
                    displayed amounts may not reflect the exact current market value.
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 h-8 text-base font-medium text-white hover:text-gray-300 focus:outline-none focus:ring-0 border-0 bg-transparent p-0 cursor-pointer"
              >
                <span>{selectedCurrency}</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-black border-black min-w-[3rem] p-1">
              <DropdownMenuRadioGroup
                value={selectedCurrency}
                onValueChange={(value) => setSelectedCurrency(value as Currency)}
              >
                <DropdownMenuRadioItem
                  value="£"
                  className="text-white hover:bg-gray-800 focus:bg-gray-800 cursor-pointer justify-center py-2"
                >
                  £
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem
                  value="$"
                  className="text-white hover:bg-gray-800 focus:bg-gray-800 cursor-pointer justify-center py-2"
                >
                  $
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem
                  value="€"
                  className="text-white hover:bg-gray-800 focus:bg-gray-800 cursor-pointer justify-center py-2"
                >
                  €
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      column: (row: Product) =>
        row.unitCost !== undefined ? (
          <span className="font-medium text-secondary-900">
            {getCurrencySymbol(selectedCurrency)}
            {formatFloatWithTwoDecimals(convertPrice(row.unitCost, selectedCurrency))}
          </span>
        ) : (
          <span className="text-secondary-500">-</span>
        ),
      styles: { minWidth: "130px", textAlign: "center" }
    });
  }

  const hasAnyHmrcUnitCost = products?.some((product) => product.hmrcUnitCost !== undefined);

  if (shouldShowUnitCost && hasAnyHmrcUnitCost) {
    const appliedHmrcRates = (["USD", "EUR"] as const)
      .map((code) => ({ code, rate: hmrcExchangeRates?.[code] }))
      .filter((entry): entry is { code: "USD" | "EUR"; rate: number } => entry.rate !== undefined);

    const hmrcCurrencyOptions: Currency[] = [
      "£",
      ...(hmrcExchangeRates?.USD ? (["$"] as Currency[]) : []),
      ...(hmrcExchangeRates?.EUR ? (["€"] as Currency[]) : [])
    ];

    // If the selected currency no longer has an HMRC rate (e.g. switched POs), fall back to GBP for display.
    const effectiveHmrcCurrency: Currency = hmrcCurrencyOptions.includes(selectedHmrcCurrency)
      ? selectedHmrcCurrency
      : "£";

    purchaseOrderProductsColumns.push({
      header: (
        <div className="flex items-center justify-center gap-4">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-help">
                  <Info className="h-3.5 w-3.5 text-secondary-300" aria-hidden="true" />
                  <span className="text-white">HMRC Unit Cost</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold text-white">HMRC-converted unit cost</p>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    Per-unit cost converted using the HMRC monthly exchange rates applied to this purchase order.
                  </p>
                  {appliedHmrcRates.length > 0 && (
                    <p className="text-xs text-gray-300 leading-relaxed">
                      Applied rates:{" "}
                      {appliedHmrcRates.map(({ code, rate }) => `${code} → GBP: ${rate.toFixed(4)}`).join(" · ")}
                    </p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {hmrcCurrencyOptions.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 h-8 text-base font-medium text-white hover:text-gray-300 focus:outline-none focus:ring-0 border-0 bg-transparent p-0 cursor-pointer"
                >
                  <span>{effectiveHmrcCurrency}</span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-black border-black min-w-[3rem] p-1">
                <DropdownMenuRadioGroup
                  value={effectiveHmrcCurrency}
                  onValueChange={(value) => setSelectedHmrcCurrency(value as Currency)}
                >
                  {hmrcCurrencyOptions.map((currency) => (
                    <DropdownMenuRadioItem
                      key={currency}
                      value={currency}
                      className="text-white hover:bg-gray-800 focus:bg-gray-800 cursor-pointer justify-center py-2"
                    >
                      {currency}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
      column: (row: Product) =>
        row.hmrcUnitCost !== undefined ? (
          <span className="font-medium text-secondary-900">
            {getCurrencySymbol(effectiveHmrcCurrency)}
            {formatFloatWithTwoDecimals(convertHmrcPrice(row.hmrcUnitCost, effectiveHmrcCurrency))}
          </span>
        ) : (
          <span className="text-secondary-500">-</span>
        ),
      styles: { minWidth: "160px", textAlign: "center" }
    });
  }

  const showWarehouseBreakdown = hasAnyWarehouseSplit(products) && userRole !== "VENDOR";

  purchaseOrderProductsColumns.push({
    header: showWarehouseBreakdown ? (
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs">Order Qty</span>
        <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
          <span className="text-violet-300">UK</span>
          <span className="text-orange-300">EU</span>
        </div>
      </div>
    ) : (
      ProductColumnHeader.ORDER_QUANTITY
    ),
    column: (row) =>
      showWarehouseBreakdown ? (
        <div className="flex flex-col items-center gap-0.5">
          <span className="font-semibold text-secondary-900 tabular-nums">{row.quantity}</span>
          <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
            <span className="text-right text-violet-600">{row.ukQuantity ?? 0}</span>
            <span className="text-left text-orange-600">{row.euQuantity ?? 0}</span>
          </div>
        </div>
      ) : (
        <span className="font-semibold text-secondary-900">{row.quantity.toString()}</span>
      ),
    styles: { minWidth: showWarehouseBreakdown ? "120px" : "100px", textAlign: "center" }
  });

  if (productConfirmationData) {
    purchaseOrderProductsColumns.push({
      header: showWarehouseBreakdown ? (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs">Confirmed Qty</span>
          <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
            <span className="text-violet-300">UK</span>
            <span className="text-orange-300">EU</span>
          </div>
        </div>
      ) : (
        ProductColumnHeader.CONFIRMED_QTY
      ),
      column: (row: Product) => {
        const confirmedQty = productConfirmationData?.find(
          (productConfirmation) => productConfirmation.productId === row.id
        )?.confirmedQty;
        const confirmedQtyClass =
          confirmedQty === undefined
            ? "text-secondary-500"
            : confirmedQty === row.quantity
              ? "text-success-700"
              : "text-error-700";

        if (!showWarehouseBreakdown) {
          return (
            <span className={`font-semibold ${confirmedQtyClass}`}>
              {confirmedQty !== undefined ? confirmedQty : "-"}
            </span>
          );
        }

        const { uk: confirmedUk, eu: confirmedEu } = splitByWarehouseRatio(
          confirmedQty,
          row.ukQuantity || 0,
          row.euQuantity || 0
        );

        return (
          <div className="flex flex-col items-center gap-0.5">
            <span className={`font-semibold tabular-nums ${confirmedQtyClass}`}>
              {confirmedQty !== undefined ? confirmedQty : "-"}
            </span>
            {confirmedQty !== undefined && (
              <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
                <span className="text-right text-violet-600">{confirmedUk}</span>
                <span className="text-left text-orange-600">{confirmedEu}</span>
              </div>
            )}
          </div>
        );
      },
      styles: { minWidth: showWarehouseBreakdown ? "130px" : "110px", textAlign: "center" }
    });
  }

  if (productConfirmationData?.some((product) => product.estRestockDate || product.isDiscontinued)) {
    purchaseOrderProductsColumns.push({
      header: ProductColumnHeader.EST_RESTOCK_DATE,
      column: (row: Product) => {
        const product = productConfirmationData?.find((product) => product.productId === row.id);
        const estRestockDate = product?.estRestockDate;
        const isDiscontinued = product?.isDiscontinued;
        if (isDiscontinued) {
          return <span className="text-secondary-500">Discontinued</span>;
        }
        return estRestockDate ? (
          <span className="text-secondary-700">{DD_MM_YYYY.formatDate(new Date(estRestockDate))}</span>
        ) : (
          <span className="text-secondary-500">-</span>
        );
      },
      styles: { minWidth: "150px" }
    });
  }

  return (
    <>
      <Table<ProductColumnHeader, Product> data={products} columns={purchaseOrderProductsColumns} />
      {selectedImage && (
        <Modal
          header={selectedImage.name}
          message={
            <div
              className="flex flex-col items-center gap-4"
              onClick={() => setSelectedImage(null)}
              role="presentation"
            >
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="max-h-[70vh] h-[400px] w-auto object-contain rounded-lg border border-secondary-200"
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          }
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
};

export { ProductTable };
